import { useEffect, useState } from 'react';
import harvest from './harvest.jsx';
import spooky from './spooky.jsx';

// Seasons: a few weeks of the year when the level dresses up.
//
// A season is a window of the calendar and whichever of four parts it wants:
//
//   id       a name, for ?season= and for the data-season attribute the
//            browser checks read
//   from/to  [month, day], both ends included, on the display's own clock.
//            A window may run over New Year: [12, 20] to [1, 6] is one season.
//            Either end may instead be a function of the year that returns
//            one, for a holiday that moves - harvest ends on Thanksgiving,
//            which is the fourth Thursday of November and not a date. It is
//            handed the year of the day being asked about.
//   reader   a replacement for READER - see castReader in sprites.js, which is
//            what keeps a stand-in the same size as the reader he stands in for
//   props    { name: sprite } - a prop of the room's own, drawn as something
//            else for the season. Names are the ones in PROPS in sprites.js;
//            the stand-in is centred where the prop would have stood.
//   Room     drawn in the room, in front of the bookcases and behind the
//            reader, and carried along with them. Handed { level, worldH,
//            ceiling } - `level` is buildLevel's, so a season can hang
//            things on its bookcases and in its gaps.
//   Screen   drawn over the room and fixed to the screen, under the status
//            bar. Handed { worldW, worldH, ceiling }.
//
// Everything a season draws is decoration and must stay that: its layers take
// no pointer events, so nothing seasonal can stand between a finger and a
// spine. Adding one is a file beside this one and a line below.
//
// The first window that contains today wins, so two that overlap are decided
// by their order here.
export const SEASONS = [spooky, harvest];

const dayOfYear = ([month, day]) => month * 100 + day;

const on = (end, year) => (typeof end === 'function' ? end(year) : end);

export function seasonFor(date, seasons = SEASONS) {
  const year = date.getFullYear();
  const today = dayOfYear([date.getMonth() + 1, date.getDate()]);
  return seasons.find(({ from, to }) => {
    const start = dayOfYear(on(from, year));
    const end = dayOfYear(on(to, year));
    return start <= end ? today >= start && today <= end : today >= start || today <= end;
  }) ?? null;
}

// ?season=<id> shows that season whatever the date, and ?season=none shows
// none. It is how a season is drawn in June, which is when somebody is going
// to be working on the one for December. Undefined when the address does not
// say, which is every real display.
function forced() {
  try {
    const id = new URLSearchParams(window.location.search).get('season');
    if (id === null) return undefined;
    return SEASONS.find((season) => season.id === id) ?? null;
  } catch {
    return undefined;
  }
}

const HOUR = 60 * 60 * 1000;

// Until the next look at the calendar: just past the coming midnight, or an
// hour, whichever is sooner. A television stays on from one season into the
// next, so this has to notice the date changing without a reload - and the
// hour is there because a timer set for ten hours on a machine that sleeps, or
// whose clock is put right, or that crosses a change of daylight saving, is a
// timer that may fire at the wrong time. An hour late is the worst it can be.
function untilNextLook(now) {
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return Math.min(HOUR, midnight - now + 1000);
}

export function useSeason() {
  const [season, setSeason] = useState(() => {
    const pinned = forced();
    return pinned === undefined ? seasonFor(new Date()) : pinned;
  });

  useEffect(() => {
    if (forced() !== undefined) return undefined;
    let timer = 0;
    const look = () => {
      const now = new Date();
      // The same object when nothing has changed, so React does nothing.
      setSeason(seasonFor(now));
      timer = setTimeout(look, untilNextLook(now));
    };
    timer = setTimeout(look, untilNextLook(new Date()));
    return () => clearTimeout(timer);
  }, []);

  return season;
}
